import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'

import CloseOutlinedIcon from '@ant-design/icons/CloseOutlined'
import EditOutlinedIcon from '@ant-design/icons/EditOutlined'
import ExclamationOutlinedIcon from '@ant-design/icons/ExclamationOutlined'
import ReloadOutlinedIcon from '@ant-design/icons/ReloadOutlined'
import WarningOutlinedIcon from '@ant-design/icons/WarningOutlined'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import InputBase from '@mui/material/InputBase'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import { isImage, isPdf, prettyBytes } from '@shared/utils'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'
import { Preview } from '@components/File'
import ImageFullScreenViewer from '@components/ImageFullScreenViewer'
import { PdfModal } from '@components/PdfViewer'

import { AttachmentStatus, displayName, NAME_MAX_LENGTH, splitExtension } from './TaskAttachments.utils'

export function UploadedTile({ file, taskOwned, onRemove }) {
  const createdAt = file.createdAt ?? file.created

  const confirmRemove = useDialog({
    component: ConfirmationV2,
    props: ({ close }) => ({
      title: taskOwned ? 'Delete file?' : 'Remove from task?',
      description: taskOwned ? 'The file will be deleted. There is no undo.' : "The document stays in the patient's chart.",
      rejectLabel: 'Cancel',
      confirmLabel: taskOwned ? 'Delete' : 'Remove',
      onReject: () => close(),
      onConfirm: () => Promise.resolve(onRemove()).then(() => close()),
    }),
  })

  return (
    <Tile
      name={file.name || file.filename}
      meta={createdAt ? dayjs(createdAt).format('L LT') : ''}
      preview={<Preview file={file} width={172} />}
      onClick={() => window.open(file.url, '_blank')}
      onRemove={onRemove ? () => confirmRemove() : undefined}
    />
  )
}

export function StagedTile({ item, onPreviewed, onRemove, onRetry, onNameChange }) {
  const objectUrl = useObjectUrl(item.file)

  const uploading = item.status === AttachmentStatus.Uploading
  const failed = item.status === AttachmentStatus.Error || Boolean(item.reason)

  if (failed) {
    return (
      <Tile
        name={displayName(item)}
        meta={item.reason}
        metaColor="error.main"
        wrapMeta
        preview={<ErrorPreview />}
        onRemove={onRemove}
        footer={
          onRetry && (
            <Link component="button" type="button" variant="subtitle2" underline="hover" onClick={onRetry} sx={styles.action}>
              <ReloadOutlinedIcon style={{ fontSize: 12 }} />
              Retry
            </Link>
          )
        }
      />
    )
  }

  if (uploading) {
    return (
      <Tile
        name={displayName(item)}
        meta="Uploading…"
        preview={
          <>
            <Box sx={{ position: 'absolute', inset: 0, opacity: 0.45 }}>
              <Preview file={localPreviewSource(item.file, objectUrl)} width={172} />
            </Box>
            <LinearProgress sx={styles.progress} />
          </>
        }
      />
    )
  }

  const preview = <Preview file={localPreviewSource(item.file, objectUrl)} width={172} />
  const meta = `${item.previewed ? 'Previewed' : 'Click to preview'} · ${prettyBytes(item.file.size)}`
  const metaColor = item.previewed ? 'success.dark' : 'warning.dark'
  const badge = !item.previewed && (
    <Box sx={styles.badge}>
      <ExclamationOutlinedIcon style={{ fontSize: 14 }} />
    </Box>
  )
  const nameField = onNameChange && <NameField item={item} onChange={onNameChange} />

  if (isPdf(item.file.type)) {
    return (
      <PdfStagedTile
        item={item}
        meta={meta}
        metaColor={metaColor}
        badge={badge}
        preview={preview}
        nameField={nameField}
        onPreviewed={onPreviewed}
        onRemove={onRemove}
      />
    )
  }

  return (
    <ImageFullScreenViewer image={objectUrl}>
      {(openViewer) => (
        <Tile
          name={item.file.name}
          meta={meta}
          metaColor={metaColor}
          preview={preview}
          badge={badge}
          nameField={nameField}
          onClick={() => {
            openViewer()
            onPreviewed()
          }}
          onRemove={onRemove}
        />
      )}
    </ImageFullScreenViewer>
  )
}

function PdfStagedTile({ item, meta, metaColor, badge, preview, nameField, onPreviewed, onRemove }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Tile
        name={item.file.name}
        meta={meta}
        metaColor={metaColor}
        preview={preview}
        badge={badge}
        nameField={nameField}
        onClick={() => {
          setOpen(true)
          onPreviewed()
        }}
        onRemove={onRemove}
      />
      <PdfModal
        initialScale={1.2}
        file={item.file}
        filename={item.file.name}
        title={item.file.name}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

function Tile({ name, meta, metaColor = 'text.secondary', preview, badge, footer, nameField, wrapMeta = false, onClick, onRemove }) {
  return (
    <Card sx={[styles.card, Boolean(onClick) && { cursor: 'pointer' }]} onClick={onClick}>
      <Box sx={styles.thumb}>
        {preview}
        {badge}
        {onRemove && (
          <IconButton
            variant="contained"
            color="secondary"
            shape="rounded"
            size="small"
            sx={styles.remove}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            <CloseOutlinedIcon style={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Stack sx={styles.caption}>
        {nameField ?? (
          <Typography noWrap variant="subtitle1">
            {name}
          </Typography>
        )}
        {meta && (
          <Typography variant="subtitle2" sx={{ color: metaColor }} noWrap={!wrapMeta}>
            {meta}
          </Typography>
        )}
        {footer}
      </Stack>
    </Card>
  )
}

function localPreviewSource(file, objectUrl) {
  return { url: isImage(file.type) ? objectUrl : file, mimetype: file.type }
}

function NameField({ item, onChange }) {
  const [, extension] = splitExtension(item.file.name)

  return (
    <InputBase
      fullWidth
      value={item.name}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      startAdornment={<EditOutlinedIcon style={{ fontSize: 12, marginRight: 4, flexShrink: 0 }} />}
      endAdornment={
        <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
          {extension}
        </Typography>
      }
      slotProps={{ input: { maxLength: NAME_MAX_LENGTH } }}
      sx={styles.nameField}
    />
  )
}

function ErrorPreview() {
  return (
    <Box sx={styles.errorPreview}>
      <WarningOutlinedIcon style={{ fontSize: 28 }} />
    </Box>
  )
}

function useObjectUrl(file) {
  const url = useMemo(() => (file && isImage(file.type) ? URL.createObjectURL(file) : null), [file])

  useEffect(() => () => url && URL.revokeObjectURL(url), [url])

  return url
}

const styles = {
  card: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'grey.100',
    boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
  },
  thumb: {
    height: 92,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'grey.200',
  },
  nameField: {
    typography: 'subtitle1',
    height: 24,
    px: 0.5,
    borderRadius: 0.5,
    border: '1px solid',
    borderColor: 'grey.500',
    color: 'text.secondary',
    backgroundColor: 'background.paper',
    '&.Mui-focused': { borderColor: 'primary.main', boxShadow: (theme) => theme.customShadows.primary },
    '& input': { p: 0, color: 'text.primary' },
  },
  caption: {
    backgroundColor: 'grey.200',
    px: 1,
    py: 0.5,
  },
  remove: {
    position: 'absolute',
    top: 6,
    right: 6,
  },
  badge: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'warning.main',
    color: 'warning.contrastText',
  },
  progress: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 4,
  },
  errorPreview: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'error.lighter',
    color: 'error.main',
  },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
  },
}

export const tileGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(172px, 1fr))',
  gap: 1,
}
